import React, { useState, useMemo, memo } from "react";
import ReactSelect from "react-select";
import { isDuplicateSkill } from "../../utils/skillUtils";
import { urtoast } from "../Layouts/CustomPopup/eventEmitter";
import AsteriskLabel from "./AsteriskLabel";

const SkillSelector = memo(({
    allSkills = [],
    selectedSkills,
    setSelectedSkills,
    setFormData,
    validation,
    placeholder = "Select competencies",
}) => {
    const [selectSkill, setSelectSkill] = useState(null);
    const [addManual, setAddManual] = useState(false);
    const [manualSkill, setManualSkill] = useState("");

    // ✅ Add skill from dropdown or manual input
    const handleAddSkill = (skill) => {
        if (!skill) return;

        const skillId = skill.id ?? skill.value;
        const skillTitle = skill.title ?? skill.label;

        if (isDuplicateSkill(skillId, selectedSkills)) {
            urtoast("Duplicate Skill Not allowed.", 2);
            return;
        }

        const newSkill = { id: skillId, title: skillTitle };
        const updatedSkills = Array.isArray(selectedSkills) ? [...selectedSkills, newSkill] : [newSkill];

        setSelectedSkills(updatedSkills);
        setFormData((prev) => ({ ...prev, skills: updatedSkills }));
    };

    // ✅ Delete skill
    const handleDeleteSkill = (id) => {
        const updatedSkills = selectedSkills?.filter((skill) => skill.id !== id);
        setSelectedSkills(updatedSkills);
        setFormData((prev) => ({ ...prev, skills: updatedSkills }));
    };

    // ✅ Add manual skill
    const handleAddManualSkill = () => {
        const trimmed = manualSkill.trim();
        if (!trimmed) return;

        const duplicate =
            isDuplicateSkill(trimmed, selectedSkills) ||
            (Array.isArray(allSkills) &&
                allSkills.some((skill) => skill.title?.toLowerCase() === trimmed.toLowerCase()));

        if (duplicate) {
            urtoast("Duplicate Skill Not allowed.", 2);
            return;
        }

        handleAddSkill({ id: trimmed, title: trimmed });
        setManualSkill("");
        setAddManual(false);
    };

    const availableOptions = useMemo(() => {
        if (!Array.isArray(allSkills)) return [];
        return allSkills
            .filter((skill) => !isDuplicateSkill(skill.id, selectedSkills))
            .map((skill) => ({ value: skill.id, label: skill.title }));
    }, [allSkills, selectedSkills]);

    return (
        <div className="skill-selector">
            <div className="form-group skill-sec">
                <label>{placeholder} <AsteriskLabel /></label>
                <div className="skill-select">
                    <ReactSelect
                        options={availableOptions}
                        value={selectSkill}
                        onChange={(option) => {
                            handleAddSkill(option);
                            setSelectSkill(null);
                        }}
                        placeholder={placeholder}
                        isClearable
                    />
                    <button
                        className="add-plus btn"
                        type="button"
                        onClick={() => setAddManual(!addManual)}
                    >
                        + Add manually
                    </button>
                </div>

                {addManual && (
                    <div className="manualskill-input">
                        <input
                            type="text"
                            value={manualSkill}
                            onChange={(e) => setManualSkill(e.target.value)}
                            placeholder="Type a competency"
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleAddManualSkill();
                                }
                            }}
                        />
                        <button type="button" onClick={handleAddManualSkill}>
                            Add
                        </button>
                        <button type="button" onClick={() => setAddManual(false)}>
                            Close
                        </button>
                    </div>
                )}

                <div className="add-skills">
                    {Array.isArray(selectedSkills) && selectedSkills.map((skill) => (
                        <p key={skill.id} >
                            {skill.title}{" "}
                            <span type="button" onClick={() => handleDeleteSkill(skill.id)}>
                                x
                            </span>
                        </p>
                    ))}
                </div>

                {validation && selectedSkills?.length < 3 && (
                    <p style={{ color: "red" }}>At least 3 competencies required</p>
                )}
            </div>
        </div>
    );
});

export default SkillSelector;
